import { useTranslation } from "react-i18next";

import { DayColumnTargets } from "@/components/day-columns";
import { formatDay, formatDayMonth, formatNumber, formatPercent, weekdayOf } from "@/lib/format";
import { cn } from "@/lib/utils";

import { formatDuration } from "../utils";

/** Up to this many days, every column carries its value and a day label. */
const LABELLED_DAYS = 14;

/** Share of its band a column fills, per the figure style (about 65%). */
const COLUMN_SHARE = 0.65;

/** Each series' fill by its place, bottom up: De Ceuster blauw, dc-blauw-licht, dc-lichtgrijs. */
const TONES = [
  { fill: "fill-chart-1", swatch: "bg-chart-1" },
  { fill: "fill-chart-2", swatch: "bg-chart-2" },
  { fill: "fill-chart-3", swatch: "bg-chart-3" },
] as const;

function tone(series: number): (typeof TONES)[number] {
  return TONES[Math.min(series, TONES.length - 1)] ?? TONES[0];
}

/** How a chart's values read: a share as "4%", a time as "1,2 s", a count as "3". */
export type DayBarsUnit = "share" | "ms" | "count";

/** One day as the chart takes it. */
export interface DayBarsDay {
  /** The calendar day, "2026-10-15". */
  day: string;
  /** Each series' value that day, bottom up; null for a day without one (no Odoo calls). */
  values: readonly number[] | null;
  /** The calls the values rest on, for the tooltip. */
  sample: number;
}

export interface DayBarsSpec {
  unit: DayBarsUnit;
  /** The series' names, bottom up; a legend shows from two series on. */
  series: readonly string[];
  /** What the sample counts, for the tooltip: "Odoo-oproepen". */
  sampleLabel: string;
  days: readonly DayBarsDay[];
}

/** A value in the chart's unit. */
export function formatValue(unit: DayBarsUnit, value: number): string {
  if (unit === "share") {
    return formatPercent(value);
  }
  return unit === "ms" ? formatDuration(value) : formatNumber(value);
}

/** A day in full, for the tooltip: "do 15/10/2026". */
function dayLabel(day: string): string {
  return `${weekdayOf(day)} ${formatDay(day)}`.trim();
}

/**
 * Dependency-free column chart of one measure per day, in the style of the
 * calls chart (`<CallsChart>`): square columns about 65% of their band, the
 * series stacked bottom up (De Ceuster blauw, then dc-blauw-licht, then
 * dc-lichtgrijs) with 2 px gaps in the card's colour, on a value axis from 0
 * to the busiest day. No thresholds: the figures speak for themselves. Up to
 * 14 days every column carries its value; longer periods get one gridline at
 * the axis' top. A day's figures show on hover, on a tap and on the
 * keyboard's focus, and each column says them to screen readers
 * (`<DayColumnTargets>`).
 */
export function DayBars({ spec, labelledBy }: { spec: DayBarsSpec; labelledBy: string }) {
  const { t } = useTranslation();
  const totals = spec.days.map((day) =>
    day.values ? day.values.reduce((a, b) => a + b, 0) : null,
  );
  const highest = Math.max(0, ...totals.map((total) => total ?? 0));
  const max = highest > 0 ? highest : 1;
  const band = spec.days.length > 0 ? 100 / spec.days.length : 100;
  const labelled = spec.days.length <= LABELLED_DAYS;
  const stacked = spec.series.length > 1;
  const value = (v: number) => formatValue(spec.unit, v);
  const first = spec.days.at(0);
  const last = spec.days.at(-1);

  const rows = (day: DayBarsDay, total: number | null): string[] => {
    const lines = [dayLabel(day.day)];
    if (day.values) {
      spec.series.forEach((label, index) =>
        lines.push(t("speed.tooltipRow", { label, value: value(day.values?.[index] ?? 0) })),
      );
      if (stacked && total !== null) {
        lines.push(t("speed.tooltipRow", { label: t("speed.total"), value: value(total) }));
      }
    }
    lines.push(t("speed.tooltipRow", { label: spec.sampleLabel, value: formatNumber(day.sample) }));
    return lines;
  };

  return (
    <figure aria-labelledby={labelledBy}>
      {stacked && (
        <div className="text-foreground mb-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-semibold">
          {spec.series.map((label, index) => (
            <span key={label} className="flex items-center gap-1.5">
              <span className={cn("inline-block h-2.5 w-2.5", tone(index).swatch)} />
              {label}
            </span>
          ))}
        </div>
      )}
      <div className="relative h-40 pt-5">
        {!labelled && (
          <div aria-hidden="true" className="border-border absolute inset-x-0 top-5 border-t-2">
            <span className="text-muted absolute -top-5 left-0 text-xs">{value(max)}</span>
          </div>
        )}
        <div aria-hidden="true" className="border-border h-full border-b-2">
          <svg className="h-full w-full overflow-visible">
            {spec.days.map((day, index) => {
              const x = (index + (1 - COLUMN_SHARE) / 2) * band;
              const width = COLUMN_SHARE * band;
              const total = totals[index] ?? null;
              let below = 0;
              const segments = (day.values ?? []).flatMap((v, series) => {
                const segment = {
                  series,
                  y: 100 - ((below + v) / max) * 100,
                  height: (v / max) * 100,
                };
                below += v;
                return v > 0 ? [segment] : [];
              });
              return (
                <g key={day.day}>
                  {segments.map((segment) => (
                    <rect
                      key={segment.series}
                      x={`${x}%`}
                      width={`${width}%`}
                      y={`${segment.y}%`}
                      height={`${segment.height}%`}
                      className={tone(segment.series).fill}
                    />
                  ))}
                  {segments.slice(1).map((segment) => (
                    <line
                      key={`gap-${segment.series}`}
                      x1={`${x}%`}
                      x2={`${x + width}%`}
                      y1={`${segment.y + segment.height}%`}
                      y2={`${segment.y + segment.height}%`}
                      strokeWidth={2}
                      className="stroke-card"
                    />
                  ))}
                  {labelled && total !== null && (
                    <text
                      x={`${(index + 0.5) * band}%`}
                      y={`${100 - (total / max) * 100}%`}
                      dy={-6}
                      textAnchor="middle"
                      className="fill-foreground text-xs font-semibold"
                    >
                      {value(total)}
                    </text>
                  )}
                </g>
              );
            })}
          </svg>
        </div>
        <DayColumnTargets
          columns={spec.days.map((day, index) => {
            const lines = rows(day, totals[index] ?? null);
            return {
              day: day.day,
              label: lines.join(", "),
              tooltip: lines.map((line, lineIndex) => (
                <span key={lineIndex} className={cn("block", lineIndex === 0 && "font-semibold")}>
                  {line}
                </span>
              )),
            };
          })}
          className="absolute inset-x-0 top-5 bottom-0"
        />
      </div>
      {labelled ? (
        <div className="text-muted mt-2 flex text-xs" aria-hidden="true">
          {spec.days.map((day) => (
            <span key={day.day} className="flex flex-1 flex-col items-center leading-tight">
              <span>{weekdayOf(day.day)}</span>
              <span className="tabular-nums">{formatDayMonth(day.day)}</span>
            </span>
          ))}
        </div>
      ) : (
        <div className="text-muted mt-2 flex justify-between text-xs" aria-hidden="true">
          <span>{first && dayLabel(first.day)}</span>
          <span>{last && dayLabel(last.day)}</span>
        </div>
      )}
    </figure>
  );
}
